import {
  ScheduleApiData,
  ScheduleApiShiftItem,
  ScheduleData,
  ScheduleSection,
  ScheduleShiftItem,
  ScheduleWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";
import type {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "web-apps/company/components/schedule/types/schedule.types";

export type ScheduleActions =
  | { type: "SET_IS_LOADING"; payload: { value: boolean; requestId: number } }
  | {
      type: "SET_IS_LOADING_MORE";
      payload: { value: boolean; requestId: number };
    }
  | { type: "SET_REQUEST_ERROR"; payload: ScheduleState["error"] }
  | {
      type: "SET_SCHEDULE";
      payload: {
        data: ScheduleApiData | null;
        requestId: number;
        append: boolean;
        page: number;
        pageSize: number;
      };
    }
  | { type: "SET_ACTIVE_REQUEST_ID"; payload: number | null }
  | { type: "RESET_SCHEDULE" }
  | { type: "SET_PREFERENCES"; payload: SchedulePreferences }
  | {
      type: "UPDATE_SHIFT";
      payload: { gigShiftId: number; patch: Partial<ScheduleShiftItem> };
    };

export type SchedulePreferences = {
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
};

export type ScheduleState = {
  isLoading: boolean;
  isLoadingMore: boolean;
  schedule: ScheduleData | null;
  error: { message: string; err: Error } | null;
  activeRequestId: number | null;
  page: number;
  pageSize: number;
  preferences: SchedulePreferences;
};

export const scheduleInitState: ScheduleState = {
  isLoading: false,
  isLoadingMore: false,
  schedule: null,
  error: null,
  activeRequestId: null,
  page: 0,
  pageSize: 0,
  preferences: {
    groupByOption: "team",
    intervalOption: "weekly",
    sizeOption: "large",
  },
};

const toScheduleShiftItem = (
  item: ScheduleApiShiftItem,
  externalId: string,
): ScheduleShiftItem => ({
  gigId: item.gigId,
  gigType: item.gigType,
  gigShiftId: item.gigShiftId,
  startTime: item.startTime,
  endTime: item.endTime,
  status: item.status,
  timeReportIds: item.timeReportIds,
  details: item.details,
  workerId: item.workerId,
  workerName: item.workerName,
  workerExternalId: item.workerExternalId ?? externalId ?? null,
});

const normalizeScheduleData = (data: ScheduleApiData): ScheduleData => {
  const rows: ScheduleWorkerRow[] = [];
  data.items.forEach((row) => {
    const sectionsIndex = new Map<number, ScheduleSection>();
    const rawItems = Array.isArray(row.items) ? row.items : [];
    if (rawItems.length === 0) return;
    rawItems.forEach((item) => {
      const sectionId = item.workGroupId;
      const existingSection = sectionsIndex.get(sectionId);
      const normalizedItem = toScheduleShiftItem(item, row.externalId);
      if (existingSection) {
        existingSection.items.push(normalizedItem);
        return;
      }
      sectionsIndex.set(sectionId, {
        id: sectionId,
        text: item.workGroupName,
        unitName: item.companyUnitName,
        items: [normalizedItem],
      });
    });
    rows.push({
      id: row.id,
      heading: row.heading,
      text: row.text,
      text2: row.text2,
      isWorker: row.isWorker,
      paymentType: row.paymentType,
      sections: Array.from(sectionsIndex.values()),
      externalId: row.externalId,
    });
  });
  return {
    items: rows,
    total: data.total,
  };
};

export const scheduleStateReducer = (
  state: ScheduleState,
  action: ScheduleActions,
): ScheduleState => {
  switch (action.type) {
    case "SET_IS_LOADING": {
      if (action.payload.requestId !== state.activeRequestId) return state;
      return { ...state, isLoading: action.payload.value };
    }
    case "SET_IS_LOADING_MORE": {
      if (action.payload.requestId !== state.activeRequestId) return state;
      return { ...state, isLoadingMore: action.payload.value };
    }
    case "SET_REQUEST_ERROR": {
      return { ...state, error: action.payload };
    }
    case "SET_SCHEDULE": {
      if (action.payload.requestId !== state.activeRequestId) return state;
      if (!action.payload.data) {
        return {
          ...state,
          schedule: null,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
        };
      }
      const normalizedData = normalizeScheduleData(action.payload.data);
      const nextItems = action.payload.append
        ? [...(state.schedule?.items ?? []), ...normalizedData.items]
        : normalizedData.items;
      return {
        ...state,
        schedule: {
          items: nextItems,
          total: normalizedData.total,
        },
        page: action.payload.page,
        pageSize: action.payload.pageSize,
      };
    }
    case "SET_ACTIVE_REQUEST_ID": {
      return { ...state, activeRequestId: action.payload };
    }
    case "RESET_SCHEDULE": {
      return {
        ...state,
        schedule: null,
        error: null,
        isLoading: false,
        isLoadingMore: false,
        activeRequestId: null,
        page: 0,
        pageSize: 0,
      };
    }
    case "SET_PREFERENCES": {
      return { ...state, preferences: action.payload };
    }
    case "UPDATE_SHIFT": {
      if (!state.schedule) return state;
      const { gigShiftId, patch } = action.payload;
      let hasUpdates = false;
      const nextItems = state.schedule.items.map((worker) => {
        let workerUpdated = false;
        const nextSections = worker.sections.map((section) => {
          let sectionUpdated = false;
          const nextSectionItems = section.items.map((item) => {
            if (item.gigShiftId !== gigShiftId) return item;
            hasUpdates = true;
            sectionUpdated = true;
            return { ...item, ...patch };
          });
          if (!sectionUpdated) return section;
          workerUpdated = true;
          return { ...section, items: nextSectionItems };
        });
        return workerUpdated ? { ...worker, sections: nextSections } : worker;
      });
      if (!hasUpdates) return state;
      return {
        ...state,
        schedule: {
          ...state.schedule,
          items: nextItems,
        },
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};
